/*!
 * 週期範圍選擇器（多選下拉 ＋ 上/下 N 週）—— **一組，一起用**
 *
 * # 為什麼合成一個
 *
 * 實查 4 個使用多選週期的頁面（遊戲種類數據 / B端數據分析 / 級別詳情 /
 * 級別 Top 玩家），**4 個都是選擇器與「上 N 週 / 下 N 週」成對出現**，
 * 而且每一頁都各自寫一次 `windowNav(...)`、各自排一次版、各自決定要不要
 * 包一層 `<div className="mt-2">`。結果就是同一組控制項在四頁長得不太一樣，
 * 而且「上/下 N 週」在其中一頁還飄到了離選擇器很遠的地方
 *（Brad 2026-09-10 在級別 Top 玩家頁指出：「放在現在這個奇怪的地方」）。
 *
 * 🔑 兩者本來就是同一件事的兩半：下拉決定**選哪幾週**，上/下 N 週是
 *    「維持同樣的週數、整段往前後移」。分開放就會失去這個關係。
 *
 * # 用法
 *
 *   const nav = windowNav(allWeeks, displayedWeeks);   // weekWindowUtils
 *   <TinyWeekRangePicker
 *     options={allWeeks}
 *     selectedValues={selectedWeeks}
 *     onChange={setSelectedWeeks}
 *     onApply={handleApply}
 *     onCancel={() => setSelectedWeeks([...displayedWeeks])}
 *     nav={nav}
 *     onWindowChange={applyWindow}
 *   />
 *
 * `nav` 傳 null（或 `nav.size` 為 0）就只有下拉，不畫導航。
 *
 * # layout
 *   inline   （預設）同一列並排 —— 給 header 的 scope 與控制列用
 *   stacked  下拉在上、導航在下 —— 給表單欄位（窄欄）用，搭配 `block`
 */
import React from "react";
import PropTypes from "prop-types";
import TinyMultiWeekSelector from "./TinyMultiWeekSelector";
import TinyWeekWindowNav from "./TinyWeekWindowNav";

function TinyWeekRangePicker({
  options = [],
  selectedValues = [],
  onChange,
  onApply,
  onCancel,
  nav = null,
  onWindowChange,
  layout = "inline",
  block = false,
  title = "選擇週期（可多選）",
  triggerText,
  triggerMinWidth = 200,
  dropdownAlign = "right",
  dropdownMinWidth = 320,
  dropdownMaxWidth = 480,
  maxListHeight = 300,
  align = "flex-end",
  style,
}) {
  const stacked = layout === "stacked";
  const hasNav = !!(nav && nav.size);
  // block / stacked = 填滿所在欄位 → 一律靠左並讓選擇器撐開；
  // 否則（header 的 scope）維持靠右。
  const fill = block || stacked;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: stacked ? "column" : "row",
        alignItems: stacked ? "stretch" : "center",
        justifyContent: fill ? "flex-start" : align,
        flexWrap: "wrap",
        gap: 8,
        ...style,
      }}
    >
      {/* fill 時包一層可伸縮的 flex item，選擇器才會跟欄位同寬、
          左緣才會對得上上面的 Label（min-width:0 是為了讓它縮得下去） */}
      <div style={fill ? { flex: "1 1 auto", minWidth: 0 } : undefined}>
      <TinyMultiWeekSelector
        mode="dropdown"
        title={title}
        options={options}
        selectedValues={selectedValues}
        onChange={onChange}
        onApply={onApply}
        onCancel={onCancel}
        triggerBlock={block || stacked}
        triggerText={
          triggerText !== undefined
            ? triggerText
            : selectedValues.length > 0
              ? `已選 ${selectedValues.length} 週`
              : "選擇週期"
        }
        triggerMinWidth={triggerMinWidth}
        dropdownAlign={dropdownAlign}
        dropdownMinWidth={dropdownMinWidth}
        dropdownMaxWidth={dropdownMaxWidth}
        maxListHeight={maxListHeight}
      />
      </div>
      {hasNav && (
        <TinyWeekWindowNav
          windowSize={nav.size}
          canEarlier={nav.canEarlier}
          canLater={nav.canLater}
          onEarlier={() => onWindowChange && onWindowChange(nav.earlier)}
          onLater={() => onWindowChange && onWindowChange(nav.later)}
        />
      )}
    </div>
  );
}

TinyWeekRangePicker.propTypes = {
  options: PropTypes.array,
  selectedValues: PropTypes.array,
  onChange: PropTypes.func,
  onApply: PropTypes.func,
  onCancel: PropTypes.func,
  /** `weekWindowUtils.windowNav(allWeeks, displayedWeeks)` 的回傳值；null = 不畫導航 */
  nav: PropTypes.shape({
    size: PropTypes.number,
    canEarlier: PropTypes.bool,
    canLater: PropTypes.bool,
    earlier: PropTypes.array,
    later: PropTypes.array,
  }),
  /** 按上/下 N 週時收到新的週期陣列 */
  onWindowChange: PropTypes.func,
  layout: PropTypes.oneOf(["inline", "stacked"]),
  block: PropTypes.bool,
  title: PropTypes.string,
  triggerText: PropTypes.string,
  triggerMinWidth: PropTypes.number,
  dropdownAlign: PropTypes.oneOf(["left", "right"]),
  dropdownMinWidth: PropTypes.number,
  dropdownMaxWidth: PropTypes.number,
  maxListHeight: PropTypes.number,
  /** inline 時的水平對齊，預設靠右（header scope 用） */
  align: PropTypes.string,
  style: PropTypes.object,
};

export default TinyWeekRangePicker;
